import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { QueryKey } from '../../constants/index';
import {
  CapTableIntegrityScanStatus,
  downloadCapTableScanReport,
  isCapTableScanActive,
  useCapTableScanStatus,
} from '../../hooks/company/use-scan-cap-table';
import { useInvalidateQueries } from '../../hooks/index';

const HANDLED_KEY = 'capquest.capTableScan.handledId';
const DISMISSED_KEY = 'capquest.capTableScan.dismissedId';

const readStored = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStored = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (private mode) — banner still works within the session.
  }
};

export const CapTableScanBanner: FC = () => {
  const { isActive, scan } = useCapTableScanStatus();
  const { invalidateQuery } = useInvalidateQueries(QueryKey.GET_COMPANIES);
  const [dismissedId, setDismissedId] = useState<null | string>(() => readStored(DISMISSED_KEY));

  // Finalize each completed scan exactly once, even across navigation/reload (marker in storage):
  // toast the outcome and auto-download the report when defects were found.
  useEffect(() => {
    if (!scan || isCapTableScanActive(scan.status)) return;
    if (readStored(HANDLED_KEY) === scan.id) return;

    writeStored(HANDLED_KEY, scan.id);
    invalidateQuery();

    if (scan.status === CapTableIntegrityScanStatus.FAILED) {
      toast.error(`Cap table scan failed${scan.lastError ? `: ${scan.lastError}` : ''}.`);
      return;
    }

    if (scan.errorCount === 0) {
      toast.success('All good — every company cap table totals to 100%');
      return;
    }

    toast.error(`Found ${scan.errorCount} company(ies) with cap table errors — downloading report`);
    downloadCapTableScanReport(scan);
  }, [scan, invalidateQuery]);

  if (!scan) return null;
  if (!isActive && dismissedId === scan.id) return null;

  const dismiss = () => {
    setDismissedId(scan.id);
    writeStored(DISMISSED_KEY, scan.id);
  };

  if (isActive) {
    const total = scan.totalCompanies ?? 0;
    const percent =
      total > 0 ? Math.min(100, Math.round((scan.processedCount / total) * 100)) : null;

    return (
      <div className="border-blue-100 bg-blue-50 flex flex-col gap-2 border-b px-4 py-3">
        <div className="text-blue-800 flex items-center gap-2 text-sm font-[450]">
          <span className="bg-blue-500 size-2 animate-pulse rounded-full" />
          <span>
            Scanning cap tables for errors… {scan.processedCount}
            {total ? `/${total}` : ''} {percent !== null ? `(${percent}%)` : ''}
          </span>
        </div>
        <div className="bg-blue-100 h-1.5 w-full overflow-hidden rounded-full">
          <div
            className="bg-blue-500 h-full rounded-full transition-[width] duration-500"
            style={{ width: `${percent ?? 15}%` }}
          />
        </div>
      </div>
    );
  }

  const isFailed = scan.status === CapTableIntegrityScanStatus.FAILED;
  const hasErrors = scan.errorCount > 0;

  const message = isFailed
    ? `Cap table scan failed${scan.lastError ? `: ${scan.lastError}` : ''}.`
    : hasErrors
      ? `Cap table scan complete — ${scan.errorCount} company(ies) have errors.`
      : 'Cap table scan complete — every company totals 100%.';

  const tone =
    isFailed || hasErrors
      ? 'border-red-100 bg-red-50 text-red-800'
      : 'border-green-100 bg-green-50 text-green-800';

  return (
    <div className={twMerge('flex items-center justify-between gap-3 border-b px-4 py-3', tone)}>
      <span className="text-sm font-[450]">{message}</span>
      <div className="flex items-center gap-2">
        {hasErrors && !isFailed && (
          <button
            className="border-red-200 text-red-700 hover:bg-red-50 rounded-md border bg-white px-3 py-1 text-sm font-[450]"
            onClick={() => downloadCapTableScanReport(scan)}
            type="button"
          >
            Download report
          </button>
        )}
        <button
          className="rounded-md px-2 py-1 text-sm font-[450] text-gray-500 hover:text-gray-700"
          onClick={dismiss}
          type="button"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};
